import React, { useEffect, useRef, useState } from "react";
import { BtnLoader } from "src/Loader/index";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast, successToast } from "src/utils/toasts";
import {
  loadCollectJsOnce,
  configureCollectOnce,
  resetCollectConfig,
  NMI_IDS,
} from "src/utils/nmiCollectManager";

export interface PayBillNmiPaymentProps {
  storeCorrelationId: string;
  customerOrderCorrelationId: string;
  onSuccess: () => void;
}

const PayBillNmiPayment = ({
  storeCorrelationId,
  customerOrderCorrelationId,
  onSuccess,
}: PayBillNmiPaymentProps) => {
  const { axBe } = useAxiosInterceptor();
  const [tokenKey, setTokenKey] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isCollectJSReady, setIsCollectJSReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState("");

  const processedTokensRef = useRef<Set<string>>(new Set());
  const isProcessingRef = useRef(false);
  const cardholderNameRef = useRef("");
  const waitingForTokenRef = useRef(false);
  const configureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    cardholderNameRef.current = cardholderName;
  }, [cardholderName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axBe.get("payment/config", {
          params: { storeCorrelationId },
        });
        if (cancelled) return;
        const key = (data as { tokenizationKey?: string })?.tokenizationKey;
        if (!key) {
          setConfigError("Payment configuration is not available for this store.");
          failureToast("Failed to load payment configuration");
          return;
        }
        setTokenKey(key);
      } catch {
        if (!cancelled) {
          setConfigError("Failed to load payment configuration.");
          failureToast("Failed to load payment configuration");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [axBe, storeCorrelationId]);

  useEffect(() => {
    if (!tokenKey) return;

    let cancelled = false;
    const configKey = `${storeCorrelationId}:${customerOrderCorrelationId}:owner-pay-bill:${tokenKey}`;
    const MAX_TICKS = 50;
    let tickCount = 0;

    const handleTokenResponse = async (response: any) => {
      if (!response?.token) {
        waitingForTokenRef.current = false;
        setIsLoading(false);
        failureToast(response?.message || "Payment failed. Please check your card details.");
        return;
      }

      if (processedTokensRef.current.has(response.token)) {
        waitingForTokenRef.current = false;
        setIsLoading(false);
        return;
      }

      if (!cardholderNameRef.current.trim()) {
        waitingForTokenRef.current = false;
        setIsLoading(false);
        failureToast("Please enter cardholder name");
        return;
      }

      if (isProcessingRef.current) {
        waitingForTokenRef.current = false;
        setIsLoading(false);
        return;
      }

      const paymentToken = response.token;
      isProcessingRef.current = true;
      processedTokensRef.current.add(paymentToken);
      setIsLoading(true);

      try {
        await axBe.post("payment/checkout/customerPayment", {
          paymentToken,
          customerOrderCorrelationId,
          storeCorrelationId,
        });
        successToast("Payment successful");
        onSuccessRef.current();
      } catch (error: any) {
        processedTokensRef.current.delete(paymentToken);
        const errorData = error?.response?.data || {};
        const errorMessage =
          errorData?.message ||
          errorData?.error ||
          error?.message ||
          "Payment processing failed. Please try again.";
        failureToast(errorMessage);
      } finally {
        isProcessingRef.current = false;
        setIsLoading(false);
        waitingForTokenRef.current = false;
      }
    };

    loadCollectJsOnce(tokenKey)
      .then(() => {
        if (cancelled) return;
        setScriptLoaded(true);

        const id = setInterval(() => {
          if (cancelled) return;
          tickCount += 1;

          configureCollectOnce({
            configKey,
            tokenizationKey: tokenKey,
            paymentSelector: `#${NMI_IDS.payButton}`,
            fieldSelectors: {
              ccnumber: `#${NMI_IDS.ccnumber}`,
              ccexp: `#${NMI_IDS.ccexp}`,
              cvv: `#${NMI_IDS.cvv}`,
            },
            callback: (response: any) => {
              void handleTokenResponse(response);
            },
            onFieldsReady: () => {
              if (cancelled) return;
              if (configureIntervalRef.current) {
                clearInterval(configureIntervalRef.current);
                configureIntervalRef.current = null;
              }
              setIsCollectJSReady(true);
            },
            onTimeout: () => {
              failureToast("Payment form timed out. Please try again.");
            },
          });

          if (tickCount >= MAX_TICKS && configureIntervalRef.current) {
            clearInterval(configureIntervalRef.current);
            configureIntervalRef.current = null;
            failureToast("Payment form failed to initialize. Close and try again.");
          }
        }, 120);

        configureIntervalRef.current = id;
      })
      .catch(() => {
        if (!cancelled) failureToast("Failed to load payment form.");
      });

    return () => {
      cancelled = true;
      if (configureIntervalRef.current) {
        clearInterval(configureIntervalRef.current);
        configureIntervalRef.current = null;
      }
    };
  }, [tokenKey, storeCorrelationId, customerOrderCorrelationId, axBe]);

  useEffect(() => {
    return () => {
      resetCollectConfig();
    };
  }, []);

  const handlePayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isProcessingRef.current || isLoading) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (!cardholderNameRef.current.trim()) {
      e.preventDefault();
      e.stopPropagation();
      failureToast("Please enter cardholder name");
      return false;
    }
    waitingForTokenRef.current = true;
    setIsLoading(true);
    setTimeout(() => {
      if (waitingForTokenRef.current && !isProcessingRef.current) {
        waitingForTokenRef.current = false;
        setIsLoading(false);
      }
    }, 15000);
    return true;
  };

  if (configError) {
    return <p className="mt-4 text-sm text-red-600">{configError}</p>;
  }

  if (!tokenKey) {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm text-[#666666]">
        <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-[#FD7657] border-t-transparent" aria-hidden />
        Loading payment options…
      </div>
    );
  }

  return (
    <div className="relative mt-4 flex min-h-0 w-full flex-1 flex-col">
      {!scriptLoaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80">
          <span className="text-sm text-[#666666]">Loading secure card fields…</span>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4">
          <div>
            <label htmlFor="paybill-cardholder" className="mb-2 block text-sm font-medium text-[#374151]">
              Cardholder name
            </label>
            <input
              id="paybill-cardholder"
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="Name on card"
              autoComplete="cc-name"
              disabled={isLoading}
              className="box-border h-[42px] w-full rounded-[8px] border border-[#F1F1F2] bg-white px-[16px] font-sans text-sm text-[#252525] antialiased placeholder:font-sans placeholder:text-xs placeholder:text-[#9CA3AF] focus:border-[#FD7657] focus:outline-none focus:ring-0 focus:ring-offset-0"
            />
          </div>
          <div>
            <span className="mb-2 block text-sm font-medium text-[#374151]">Card number</span>
            <div
              id={NMI_IDS.ccnumber}
              className="box-border flex min-h-[42px] w-full items-center rounded-[8px] border border-[#F1F1F2] bg-white px-[16px] py-1"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <span className="mb-2 block text-sm font-medium text-[#374151]">Expiration</span>
              <div
                id={NMI_IDS.ccexp}
                className="box-border flex min-h-[42px] w-full items-center rounded-[8px] border border-[#F1F1F2] bg-white px-[16px] py-1"
              />
            </div>
            <div>
              <span className="mb-2 block text-sm font-medium text-[#374151]">CVV</span>
              <div
                id={NMI_IDS.cvv}
                className="box-border flex min-h-[42px] w-full items-center rounded-[8px] border border-[#F1F1F2] bg-white px-[16px] py-1"
              />
            </div>
          </div>
        </div>
        <div className="mt-auto pt-6">
          <button
            type="button"
            id={NMI_IDS.payButton}
            onClick={handlePayClick}
            disabled={isLoading || !isCollectJSReady || !cardholderName.trim()}
            className="flex w-full min-h-[44px] items-center justify-center rounded-lg bg-[#FD7657] py-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isLoading ? (
              <>
                <BtnLoader width={5} height={5} />
                <span className="ml-2">Processing…</span>
              </>
            ) : (
              "Pay now"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayBillNmiPayment;
